import { Menu } from 'lucide-react'
import * as React from 'react'

import useMediaQuery from '@/hooks/use-media-query'

import MarkdownPreview from '@/components/markdown/markdown-preview'
import TableOfContents from '@/components/markdown/table-of-contents'
import { Button } from '@/components/ui/button'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'

import { TextContentViewerToolbar } from './text-content-viewer-toolbar'
import { PROSE_SIZE_MAP } from '../constants'
import type { TextLayout, TextSize, TextTheme } from '../types'

export function TextContentViewer({ contentData }: { contentData?: string }) {
  const content = (contentData as string) || ''
  const [theme, setTheme] = React.useState<TextTheme>('light')
  const [textSize, setTextSize] = React.useState<TextSize>('base')
  const [layout, setLayout] = React.useState<TextLayout>('sidebar')
  const isMobile = useMediaQuery('(max-width: 768px)')
  const showTocSidebar = layout === 'sidebar' && !isMobile

  const [resolvedTheme, setResolvedTheme] = React.useState<'light' | 'dark'>(theme === 'system' ? 'light' : theme)
  React.useEffect(() => {
    if (theme !== 'system') {
      setResolvedTheme(theme)
      return
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const update = () => setResolvedTheme(mq.matches ? 'dark' : 'light')
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [theme])

  const paperClass = resolvedTheme === 'dark' ? 'bg-zinc-900 text-zinc-100' : 'bg-white text-zinc-900'

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <div className="flex-shrink-0">
        <TextContentViewerToolbar
          theme={theme}
          resolvedTheme={resolvedTheme}
          setTheme={setTheme}
          textSize={textSize}
          setTextSize={setTextSize}
          layout={layout}
          setLayout={setLayout}
        />
      </div>
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {showTocSidebar ? (
          <ResizablePanelGroup direction="horizontal" className="h-full min-h-0">
            <ResizablePanel defaultSize={20} minSize={15} maxSize={40} className="min-h-0 overflow-hidden">
              <aside
                className={`flex h-full max-h-[calc(100vh-3.5rem)] min-h-0 flex-col overflow-y-auto overflow-x-hidden border-r-2 p-4 ${
                  resolvedTheme === 'dark'
                    ? 'bg-zinc-800 text-zinc-100 border-zinc-600 shadow-[2px_0_8px_rgba(0,0,0,0.2)]'
                    : 'bg-white border-border'
                }`}
              >
                <TableOfContents content={content} theme={resolvedTheme} />
              </aside>
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={80} minSize={50} className="min-h-0 overflow-hidden">
              <main
                className={`h-full min-h-0 overflow-y-auto overflow-x-hidden p-6 lg:p-10 ${paperClass}`}
                data-theme={resolvedTheme}
              >
                <article
                  className={`prose max-w-none ${PROSE_SIZE_MAP[textSize]} prose-pre:overflow-x-auto ${
                    resolvedTheme === 'dark' ? 'prose-invert' : ''
                  }`}
                >
                  <MarkdownPreview enableImagePreview>{content}</MarkdownPreview>
                </article>
              </main>
            </ResizablePanel>
          </ResizablePanelGroup>
        ) : (
          <main className={`flex-1 min-w-0 overflow-y-auto p-6 lg:p-10 ${paperClass}`} data-theme={resolvedTheme}>
            <article
              className={`prose max-w-none ${PROSE_SIZE_MAP[textSize]} prose-pre:overflow-x-auto ${
                resolvedTheme === 'dark' ? 'prose-invert' : ''
              }`}
            >
              <MarkdownPreview enableImagePreview>{content}</MarkdownPreview>
            </article>
          </main>
        )}
      </div>
      {isMobile && layout === 'sidebar' && (
        <div className="fixed bottom-6 left-6 z-50">
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="secondary"
                size="icon"
                className="size-10 rounded-full bg-primary text-primary-foreground shadow-lg"
                aria-label="Open Table of Contents"
              >
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className={`w-[80%] sm:w-[350px] overflow-y-auto ${
                resolvedTheme === 'dark' ? 'bg-zinc-800 text-zinc-100 border-l-2 border-zinc-600' : ''
              }`}
            >
              <div className="py-2">
                <TableOfContents content={content} theme={resolvedTheme} />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      )}
    </div>
  )
}
